if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();

      this.scrollFrame = null;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.announcement_bar = document.querySelector('.announcement-bar-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Keep scroll work to one layout read per animation frame.
      window.addEventListener('scroll', this.handleScroll, { passive: true });

      // Header and announcement heights only change when their elements resize.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);

        if (this.announcement_bar) {
          this.resizeObserver.observe(this.announcement_bar);
        }
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
        this.updateHeights();
      }

      this.handleScroll();

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      this.resizeObserver?.disconnect();

      if (this.scrollFrame !== null) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
    }

    handleScroll() {
      if (this.scrollFrame !== null) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateScrollState();
      });
    }

    updateScrollState() {
      const headerTop = this.header_section
        ? this.header_section.getBoundingClientRect().top
        : this.getBoundingClientRect().top;

      document.documentElement.style.setProperty('--header-offset', `${headerTop}px`);

      if (this.classList.contains('header-sticky--active')) {
        const offset = parseInt(headerTop, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }

    handleResize(entries) {
      if (!Array.isArray(entries)) {
        this.updateHeights();
        this.handleScroll();
        return;
      }

      entries.forEach((entry) => {
        const borderBox = Array.isArray(entry.borderBoxSize)
          ? entry.borderBoxSize[0]
          : entry.borderBoxSize;
        const height = borderBox?.blockSize ?? entry.target.clientHeight;

        if (entry.target === this) {
          document.documentElement.style.setProperty('--header-height', `${height}px`);
        } else if (entry.target === this.announcement_bar) {
          document.documentElement.style.setProperty('--announcement-height', `${height}px`);
        }
      });

      this.handleScroll();
    }

    updateHeights() {
      document.documentElement.style.setProperty('--header-height', `${this.clientHeight}px`);

      if (this.announcement_bar) {
        document.documentElement.style.setProperty('--announcement-height', `${this.announcement_bar.clientHeight}px`);
      }
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
      this.submenus = this.querySelectorAll('.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu');
    }

    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;

      // Resize on initial load
      document.fonts.ready.then(() => {
        window.addEventListener('resize', debounce(() => {
          _this.resizeSubMenus();
        }, 100));
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        const subSubmenus = submenu.querySelectorAll(':scope > .menu-item-has-children > .sub-menu');

        subSubmenus.forEach((subSubmenu) => {
          const submenuRect = subSubmenu.getBoundingClientRect();
          const submenuRightEdge = submenuRect.left + submenuRect.width;
          const submenuIsOverflowing = submenuRightEdge > window.innerWidth;

          if (submenuIsOverflowing) {
            subSubmenu.parentElement.classList.add('left-submenu');
          } else {
            subSubmenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}
